import '@fontsource-variable/inclusive-sans';
import '@fontsource-variable/inclusive-sans/wght-italic.css';
import '@fontsource-variable/museomoderno';
import { Toaster } from 'sonner';
import { Provider } from 'react-redux';
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as Sentry from '@sentry/react';
import { PostHogProvider } from '@posthog/react';
import App from './App';
import './index.css';
import store from './redux/store';

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets:
      import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS.split(','),
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

// PostHog runs only where the (publishable) project token is set — i.e. the
// prod/staging builds, not local dev.
const posthogKey = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
const posthogOptions = {
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  defaults: '2026-05-30',
  capture_pageleave: true,
  // Sentry replays are error-triggered; PostHog replays are continuous and
  // correlated with analytics events — useful for different questions.
  disable_session_recording: false,
  capture_exceptions: false,
} as const;

// MODE, not DEV: test builds force NODE_ENV=development (vite.config)
const Agentation =
  import.meta.env.MODE === 'development'
    ? lazy(() => import('agentation').then((m) => ({ default: m.Agentation })))
    : null;

const tree = (
  <>
    <App />
    <Toaster position="bottom-center" duration={1000} richColors />
    {Agentation && (
      <Suspense fallback={null}>
        <Agentation endpoint="http://localhost:4748" />
      </Suspense>
    )}
  </>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <Provider store={store}>
    <React.StrictMode>
      {posthogKey ? (
        <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
          {tree}
        </PostHogProvider>
      ) : (
        tree
      )}
    </React.StrictMode>
  </Provider>
);

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}
